import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import User from "users/user";
import ProposalSchema, { ProposalParams } from "work/proposals/_schemas/proposal-schema";
import ProposalFieldSchema from "work/proposals/proposal-fields/_schema/proposal-field-schema";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import WorkAgreementSchema from "work/work-agreements/_schemas/work-agreement-codec";
import { z } from "zod";

export type ProposalBuilderJSON = z.infer<typeof ProposalBuilderSchema.ProposalBuilderJSONZ>;
export type ProposalBuilderParams = z.infer<typeof ProposalBuilderSchema.ProposalBuilderZ>;

export default class ProposalBuilderSchema {
  public static readonly ProposalBuilderJSONZ = z.object({
    get specStack() {
      return z.array(ProposalSchema.ProposalJSONWithoutBuilderZ);
    },
    currentIndex: z.number(),
    lastSavedIndex: z.number().optional(),
    get sessionHistory() {
      return z.array(
        z.object({
          field: ProposalFieldSchema.ProposalFieldJSONZ,
          action: z.union([z.literal("Edit"), z.literal("ReEdit"), z.literal("Add"), z.literal("Remove")]),
          value: z.string(),
        }),
      );
    },
    lastUpdated: z.iso.datetime({ offset: true }),
  });

  public static readonly ProposalBuilderZ = z.object({
    get specStack() {
      return z.array(ProposalSchema.ProposalZ);
    },
    currentIndex: z.number(),
    lastSavedIndex: z.number().optional(),
    get sessionHistory() {
      return z.array(
        z.object({
          field: ProposalFieldSchema.ProposalFieldZ,
          action: z.union([z.literal("Edit"), z.literal("ReEdit"), z.literal("Add"), z.literal("Remove")]),
          value: z.string(),
        }),
      );
    },
    lastUpdated: z.instanceof(AHDate),
  });

  public static validateJSON(json: unknown): ProposalBuilderJSON {
    return parseOrThrow(ProposalBuilderSchema.ProposalBuilderJSONZ, json, {
      logError: true,
      schemaName: "ProposalBuilderSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValidJSON(json: unknown): json is ProposalBuilderJSON {
    try {
      ProposalBuilderSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static validate(values: unknown): ProposalBuilderParams {
    return parseOrThrow(ProposalBuilderSchema.ProposalBuilderZ, values, {
      logError: true,
      schemaName: "ProposalBuilderSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is ProposalBuilderParams {
    try {
      ProposalBuilderSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public deserialize(json: unknown): ProposalBuilderParams {
    const parsed = parseOrThrow(ProposalBuilderSchema.ProposalBuilderJSONZ, json, {
      logError: true,
      schemaName: "ProposalBuilderSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return this.codec.decode(parsed);
  }

  public serialize(params: ProposalBuilderParams): ProposalBuilderJSON {
    const parsed = ProposalBuilderSchema.validate(params);
    return this.codec.encode(parsed) as ProposalBuilderJSON;
  }

  private readonly codec = z.codec(ProposalBuilderSchema.ProposalBuilderJSONZ, ProposalBuilderSchema.ProposalBuilderZ, {
    decode: (data) => ({
      currentIndex: data.currentIndex,
      lastSavedIndex: data.lastSavedIndex,
      specStack: data.specStack.map((proposalJson) =>
        new ProposalSchema(this.workAgreementSchema, this.currentUser).deserialize(proposalJson),
      ),
      sessionHistory: data.sessionHistory.map((entry) => ({
        field: ProposalField.fromJSON(entry.field)!,
        action: entry.action,
        value: entry.value,
      })),
      lastUpdated: AHDate.fromJSON(data.lastUpdated)!,
    }),
    encode: (builder) => ({
      currentIndex: builder.currentIndex,
      lastSavedIndex: builder.lastSavedIndex,
      specStack: builder.specStack.map((proposalSpec) =>
        new ProposalSchema(this.workAgreementSchema, this.currentUser).serialize(proposalSpec as ProposalParams),
      ),
      lastUpdated: builder.lastUpdated.toISOString(),
      sessionHistory: builder.sessionHistory.map((entry) => ({
        field: ProposalFieldSchema.serialize(entry.field),
        action: entry.action,
        value: entry.value,
      })),
    }),
  });

  public constructor(
    private workAgreementSchema: WorkAgreementSchema,
    private currentUser: User,
  ) {}
}
